import {FormControl, FormField, Separator, Tooltip} from 'platform/components';

import {useWatch, UseFormReturn} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {usePreviewWatermarkVehicleSelectionMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleSelectionImpactPreview} from '../../../components/VehicleSelectionImpactPreview/VehicleSelectionImpactPreview';
import {VehicleSelectionSection} from '../../../components/VehicleSelectionSection/VehicleSelectionSection';
import {VEHICLE_SELECTION_PREVIEW_LIMIT} from '../../../constants/VEHICLE_SELECTION_PREVIEW_LIMIT';
import {VehicleSelectionDraft} from '../../../types/VehicleSelectionDraft';
import {WatermarkFormType} from '../types/WatermarkFormType';
import {getWatermarkVehicleSelection} from '../utils/getWatermarkVehicleSelection';
import {getWatermarkVehicleSelectionBinding} from '../utils/getWatermarkVehicleSelectionBinding';

interface WatermarkVehicleSelectionProps extends RequiredTestIdProps {
  control: FormControl<WatermarkFormType>;
  formApi: UseFormReturn<WatermarkFormType>;
  // Nil for a watermark that is not saved yet.
  watermarkId: string | Nullish;
}

// "Apply to vehicles", the automatic assignment and the impact preview of a watermark. Only the
// fields of the preview draft are watched, so the rest of the form does not re-render the card.
export function WatermarkVehicleSelection(props: WatermarkVehicleSelectionProps) {
  const [previewWatermarkVehicleSelection] = usePreviewWatermarkVehicleSelectionMutation();
  const [vehicleSelectionMode, filter, autoAssign] = useWatch({
    control: props.control,
    name: ['vehicleSelectionMode', 'filter', 'autoAssign'],
  });
  // The preview result belongs to the draft it was computed from.
  const previewDraftKey = JSON.stringify({
    vehicleSelection: getWatermarkVehicleSelection({vehicleSelectionMode, filter}),
    autoAssign,
  });

  const previewVehicleSelection = (draft: VehicleSelectionDraft) =>
    previewWatermarkVehicleSelection({
      previewWatermarkVehicleSelectionRequestBody: {
        watermarkId: props.watermarkId ?? null,
        ...draft,
        limit: VEHICLE_SELECTION_PREVIEW_LIMIT,
      },
    }).unwrap();

  // The same rules as Save: an empty "by conditions" draft or a descending range shows its error
  // instead of previewing a filter that matches every vehicle.
  const getDraft = async () => {
    const isDraftValid = await props.formApi.trigger(['vehicleSelectionMode', 'filter']);
    if (!isDraftValid) {
      return null;
    }
    const values = props.formApi.getValues();

    return {
      vehicleSelection: getWatermarkVehicleSelection(values),
      autoAssign: values.autoAssign,
    };
  };

  return (
    <>
      <VehicleSelectionSection
        control={props.control}
        binding={getWatermarkVehicleSelectionBinding(props.formApi)}
        modeFieldName="vehicleSelectionMode"
        vehicleSelectionMode={vehicleSelectionMode}
        filter={filter}
        conditionsDescription={i18n.t(
          'entity.watermark.vehicleSelection.labels.conditionsDescription'
        )}
        data-testid={props['data-testid']}
      />
      <Separator />
      {/* The switch control has no tooltip of its own, so the whole row carries it. */}
      <Tooltip
        description={i18n.t('entity.watermark.labels.autoAssignTooltip')}
        childrenWrapperDisplay="block"
        data-testid={suffixTestId('autoAssignTooltip', props)}
      >
        <FormField
          type="switch"
          control={props.control}
          name="autoAssign"
          label={i18n.t('entity.watermark.labels.autoAssign')}
          helperText={i18n.t('entity.watermark.labels.autoAssignHelper')}
          data-testid={suffixTestId('autoAssign', props)}
        />
      </Tooltip>
      <Separator />
      <VehicleSelectionImpactPreview
        isStored={isNotNil(props.watermarkId)}
        previewVehicleSelection={previewVehicleSelection}
        getDraft={getDraft}
        draftKey={previewDraftKey}
        data-testid={suffixTestId('preview', props)}
      />
    </>
  );
}
